/**
 * WebSocket Service
 *
 * Handles real-time communication using Socket.IO for WebSocket-related operations.
 * Provides connectivity testing and real-time Firestore data synchronization.
 */

import { io, Socket } from "socket.io-client";
import {
  InternalSDKOperations,
  SocketConnectionMessage,
  SocketFirestoreChangeEvent,
  FirestoreChangeNotification,
  FirestoreChangeListener,
} from "../types";
import { SERVICE_NAMES } from "../core/constants";
import { LanguageCode, SUPPORTED_LANGUAGES } from "../types/languages";

/**
 * Socket.IO connection states
 */
enum ConnectionState {
  DISCONNECTED = "disconnected",
  CONNECTING = "connecting",
  CONNECTED = "connected",
  RECONNECTING = "reconnecting",
  ERROR = "error",
}

/**
 * Service class for Socket.IO communication and real-time features
 */
export class WebsocketService {
  private socket: Socket | null = null;
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private listeners: Map<string, FirestoreChangeListener> = new Map();
  private connectionPromise: Promise<void> | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectTimeout: number | null = null;
  private shouldReconnect: boolean = true;

  /**
   * Creates a new WebsocketService instance
   * @param internalOps - Internal SDK operations interface
   */
  constructor(private internalOps: InternalSDKOperations) {}

  /**
   * Connect to the Socket.IO server
   */
  async connect(): Promise<void> {
    if (this.connectionState === ConnectionState.CONNECTED) {
      return;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.shouldReconnect = true;
    this.connectionPromise = this._connect();
    try {
      await this.connectionPromise;
    } finally {
      this.connectionPromise = null;
    }
  }

  private async _connect(): Promise<void> {
    try {
      this.connectionState = ConnectionState.CONNECTING;

      const serverUrl = this.getWebSocketUrl();
      const authToken = await this.getAuthTokenForConnection();

      this.socket = io(serverUrl, {
        transports: ["websocket"],
        autoConnect: false,
        auth: {
          token: authToken,
        },
        timeout: 10000,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
      });

      return new Promise((resolve, reject) => {
        if (!this.socket) {
          reject(new Error("Socket.IO instance not created"));
          return;
        }

        this.socket.on("connect", () => {
          this.connectionState = ConnectionState.CONNECTED;
          this.reconnectAttempts = 0;
          this.clearReconnectTimeout();
          console.log("Socket.IO connected with ID:", this.socket?.id);
          resolve();
        });

        this.socket.on("connection", (data: SocketConnectionMessage) => {
          console.log("Server connection message:", data.message);
          this.notifyConnectionListeners(data);
        });

        this.socket.on(
          "firestore_change",
          (event: SocketFirestoreChangeEvent) => {
            this.handleFirestoreChange(event);
          }
        );

        this.socket.on("disconnect", (reason: string) => {
          this.connectionState = ConnectionState.DISCONNECTED;
          console.log("Socket.IO disconnected:", reason);
          this.notifyDisconnectionListeners();

          // Attempt to reconnect for various disconnect reasons
          if (
            this.shouldReconnect &&
            (reason === "io server disconnect" ||
              reason === "ping timeout" ||
              reason === "transport close" ||
              reason === "transport error")
          ) {
            this.attemptReconnect();
          }
        });

        this.socket.on("reconnect", (attemptNumber: number) => {
          this.connectionState = ConnectionState.CONNECTED;
          this.reconnectAttempts = 0;
          this.clearReconnectTimeout();
          console.log(`Socket.IO reconnected after ${attemptNumber} attempts`);
          this.notifyReconnectListeners();
        });

        this.socket.on("reconnect_attempt", (attemptNumber: number) => {
          console.log(`Socket.IO reconnection attempt ${attemptNumber}`);
        });

        this.socket.on("reconnect_error", (error: Error) => {
          console.error("Socket.IO reconnection error:", error);
        });

        this.socket.on("reconnect_failed", () => {
          this.connectionState = ConnectionState.ERROR;
          console.error("Socket.IO reconnection failed - max attempts reached");
          this.notifyErrorListeners(new Error("Socket.IO reconnection failed"));
        });

        this.socket.on("connect_error", (error: Error) => {
          this.connectionState = ConnectionState.ERROR;
          console.error("Socket.IO connection error:", error);
          this.notifyErrorListeners(error);
          reject(error);
        });

        this.socket.connect();

        setTimeout(() => {
          if (this.connectionState !== ConnectionState.CONNECTED) {
            reject(new Error("Socket.IO connection timeout"));
          }
        }, 10000);
      });
    } catch (error) {
      this.connectionState = ConnectionState.ERROR;
      throw error;
    }
  }

  /**
   * Get Socket.IO server URL using the SDK's URL construction pattern
   */
  private getWebSocketUrl(): string {
    // Constructs URL using the same pattern as other services:
    // https://sdk-websocket-service-{project}.{region}.run.app
    return this.internalOps.getServiceUrl(SERVICE_NAMES.WEBSOCKET);
  }

  /**
   * Get authentication token for Socket.IO connection
   */
  private async getAuthTokenForConnection(): Promise<string> {
    try {
      return await this.internalOps.getAuthToken();
    } catch (error) {
      throw new Error(
        "Unable to get authentication token for WebSocket connection"
      );
    }
  }

  /**
   * Handle Firestore change events from Socket.IO
   */
  private handleFirestoreChange(event: SocketFirestoreChangeEvent): void {
    try {
      const parsedData: FirestoreChangeNotification = JSON.parse(event.data);

      Array.from(this.listeners.values()).forEach((listener) => {
        listener.callback(parsedData);
      });
    } catch (error) {
      console.error("Error parsing Firestore change event:", error);
      this.notifyErrorListeners(
        new Error("Failed to parse Firestore change event")
      );
    }
  }

  /**
   * Notify connection event listeners
   */
  private notifyConnectionListeners(message: SocketConnectionMessage): void {
    Array.from(this.listeners.values()).forEach((listener) => {
      if (listener.onConnection) {
        listener.onConnection(message);
      }
    });
  }

  /**
   * Notify reconnection listeners
   */
  private notifyReconnectListeners(): void {
    Array.from(this.listeners.values()).forEach((listener) => {
      if (listener.onReconnect) {
        listener.onReconnect();
      }
    });
  }

  /**
   * Notify disconnection listeners
   */
  private notifyDisconnectionListeners(): void {
    Array.from(this.listeners.values()).forEach((listener) => {
      if (listener.onDisconnect) {
        listener.onDisconnect();
      }
    });
  }

  /**
   * Notify error listeners
   */
  private notifyErrorListeners(error: Error): void {
    Array.from(this.listeners.values()).forEach((listener) => {
      if (listener.onError) {
        listener.onError(error);
      }
    });
  }

  /** Path helpers */
  private isUserDocumentPath(path: string): boolean {
    const parts = path.split("/");
    return (
      parts.length === 2 && parts[0] === "users" && (parts[1]?.length ?? 0) > 0
    );
  }

  private isLanguageProgressPath(
    path: string,
    targetLanguage: string
  ): boolean {
    const parts = path.split("/");
    return (
      parts.length === 4 &&
      parts[0] === "users" &&
      parts[2] === "languageProgress" &&
      parts[3] === targetLanguage
    );
  }

  private tryExtractLanguageProgress(path: string): string | null {
    const parts = path.split("/");
    if (
      parts.length === 4 &&
      parts[0] === "users" &&
      parts[2] === "languageProgress" &&
      typeof parts[3] === "string" &&
      parts[3].length > 0
    ) {
      return parts[3];
    }
    return null;
  }

  private isFavoriteWordsPath(path: string, languagePair: string): boolean {
    const parts = path.split("/");
    return (
      parts.length === 4 &&
      parts[0] === "users" &&
      parts[2] === "favoriteWords" &&
      parts[3] === languagePair
    );
  }

  private tryExtractLanguagePair(path: string): string | null {
    const parts = path.split("/");
    if (
      parts.length === 4 &&
      parts[0] === "users" &&
      parts[2] === "favoriteWords" &&
      typeof parts[3] === "string" &&
      parts[3].length > 0
    ) {
      return parts[3];
    }
    return null;
  }

  private parseLanguagePair(
    pair: string
  ): { native: LanguageCode; target: LanguageCode } | null {
    const [native, target] = pair.split("_");
    if (!native || !target) return null;
    // Validate against supported languages to satisfy typing
    const isNativeSupported = SUPPORTED_LANGUAGES.includes(
      native as LanguageCode
    );
    const isTargetSupported = SUPPORTED_LANGUAGES.includes(
      target as LanguageCode
    );
    if (!isNativeSupported || !isTargetSupported) return null;
    return {
      native: native as LanguageCode,
      target: target as LanguageCode,
    };
  }

  /**
   * Add a listener for Firestore changes
   * Note: The backend automatically subscribes users based on their UID
   */
  addListener(
    callback: (change: FirestoreChangeNotification) => void,
    options?: {
      onConnection?: (message: SocketConnectionMessage) => void;
      onReconnect?: () => void;
      onError?: (error: Error) => void;
      onDisconnect?: () => void;
    }
  ): string {
    const listenerId = this.generateListenerId();
    const listener: FirestoreChangeListener = {
      id: listenerId,
      callback,
      onConnection: options?.onConnection,
      onReconnect: options?.onReconnect,
      onError: options?.onError,
      onDisconnect: options?.onDisconnect,
    };

    this.listeners.set(listenerId, listener);
    return listenerId;
  }

  /**
   * Add a single routed listener that dispatches to specific handlers
   * without requiring the app to inspect document paths.
   * This registers ONE internal listener and routes events by type.
   */
  addRoutedListener(
    routes: {
      any?: (change: FirestoreChangeNotification) => void;
      userDocument?: (change: FirestoreChangeNotification) => void;
      /** Wildcard handler for any target language progress */
      languageProgressAny?: (
        targetLanguage: LanguageCode,
        change: FirestoreChangeNotification
      ) => void;
      /** Wildcard handler for any favorite words languagePair */
      favoriteWordsAny?: (
        nativeLanguage: LanguageCode,
        targetLanguage: LanguageCode,
        change: FirestoreChangeNotification
      ) => void;
    },
    options?: {
      onConnection?: (message: SocketConnectionMessage) => void;
      onReconnect?: () => void;
      onError?: (error: Error) => void;
      onDisconnect?: () => void;
    }
  ): string {
    return this.addListener((change) => {
      try {
        routes.any?.(change);

        const doc = change.payload.event.document;
        const params = (change.payload.event.params || {}) as Record<
          string,
          unknown
        >;

        // User document
        if (this.isUserDocumentPath(doc)) {
          routes.userDocument?.(change);
          return;
        }

        // Language progress
        const targetLanguageParam =
          typeof params["targetLanguage"] === "string"
            ? (params["targetLanguage"] as string)
            : undefined;
        if (
          targetLanguageParam &&
          this.isLanguageProgressPath(doc, targetLanguageParam)
        ) {
          routes.languageProgressAny?.(
            targetLanguageParam as LanguageCode,
            change
          );
          return;
        }
        const targetLanguageFromPath = this.tryExtractLanguageProgress(doc);
        if (targetLanguageFromPath) {
          routes.languageProgressAny?.(
            targetLanguageFromPath as LanguageCode,
            change
          );
          return;
        }

        // Favorite words
        const languagePairParam =
          typeof params["languagePair"] === "string"
            ? (params["languagePair"] as string)
            : undefined;
        if (
          languagePairParam &&
          this.isFavoriteWordsPath(doc, languagePairParam)
        ) {
          const parsed = this.parseLanguagePair(languagePairParam);
          if (parsed) {
            routes.favoriteWordsAny?.(parsed.native, parsed.target, change);
          }
          return;
        }
        const languagePairFromPath = this.tryExtractLanguagePair(doc);
        if (languagePairFromPath) {
          const parsed = this.parseLanguagePair(languagePairFromPath);
          if (parsed) {
            routes.favoriteWordsAny?.(parsed.native, parsed.target, change);
          }
          return;
        }
      } catch (err) {
        options?.onError?.(err as Error);
      }
    }, options);
  }

  /**
   * Remove a specific listener
   */
  removeListener(listenerId: string): void {
    this.listeners.delete(listenerId);
  }

  /**
   * Remove all listeners
   */
  removeAllListeners(): void {
    this.listeners.clear();
  }

  /**
   * Send a message to the server (for testing or custom communication)
   */
  sendMessage(message: Record<string, unknown>): void {
    if (this.socket && this.connectionState === ConnectionState.CONNECTED) {
      this.socket.emit("message", message);
    } else {
      console.warn("Cannot send message: Socket.IO not connected");
    }
  }

  /**
   * Generate a unique listener ID
   */
  private generateListenerId(): string {
    return `listener_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current connection state
   */
  getConnectionState(): string {
    return this.connectionState;
  }

  /**
   * Check if Socket.IO is connected
   */
  isConnected(): boolean {
    return (
      this.connectionState === ConnectionState.CONNECTED &&
      this.socket?.connected === true
    );
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(
        `Max reconnection attempts (${this.maxReconnectAttempts}) reached`
      );
      this.connectionState = ConnectionState.ERROR;
      this.notifyErrorListeners(new Error("Max reconnection attempts reached"));
      return;
    }

    this.connectionState = ConnectionState.RECONNECTING;
    this.reconnectAttempts++;

    // Exponential backoff: 1s, 2s, 4s, 8s, etc. (capped at 30s)
    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts - 1),
      30000
    );

    console.log(
      `Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    );

    this.reconnectTimeout = setTimeout(async () => {
      try {
        if (this.socket && !this.socket.connected) {
          // Refresh auth token before reconnecting
          const newAuthToken = await this.getAuthTokenForConnection();
          this.socket.auth = { token: newAuthToken };
          this.socket.connect();
        }
      } catch (error) {
        console.error("Error during reconnection attempt:", error);
        this.attemptReconnect(); // Try again
      }
    }, delay) as unknown as number;
  }

  /**
   * Clear reconnection timeout
   */
  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  /**
   * Disconnect from Socket.IO server
   */
  disconnect(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimeout();
    this.removeAllListeners();

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.connectionState = ConnectionState.DISCONNECTED;
    this.reconnectAttempts = 0;
  }

  /**
   * Get active listeners count
   */
  getActiveListenersCount(): number {
    return this.listeners.size;
  }

  /**
   * Get Socket.IO instance for advanced usage
   */
  getSocket(): Socket | null {
    return this.socket;
  }
}
